/**
 * Color field — sélecteur de couleur.
 *
 * L'<input class="color-field__input"> de l'auteur reste le vrai champ du
 * formulaire (valeur #RRGGBB, saisie au clavier). Le JS insère à sa gauche une
 * pastille d'aperçu qui ouvre un popover natif : zone saturation / luminosité,
 * curseur de teinte et nuances prédéfinies.
 */

// Accès différé : le module peut être importé côté serveur (rendu SSR) sans DOM
const VALUE = typeof HTMLInputElement !== 'undefined' ? Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value') : null;
let uid = 0;

const LABELS = {
  fr: {
    open: 'Choisir une couleur', dialog: 'Choisir une couleur', current: 'couleur actuelle',
    empty: 'aucune', none: 'Aucune', noneLabel: 'Aucune couleur',
    area: 'Saturation et luminosité', areaRole: 'zone de couleur',
    areaText: 'Saturation {s} %, luminosité {v} %', hue: 'Teinte', options: 'Nuances',
    eyedropper: "Pipette : prélever une couleur à l'écran",
    invalid: 'Couleur invalide (format #RRGGBB)', required: 'Choisissez une couleur',
    tokens: {
      'brand-upjv': 'Bleu UPJV', 'brand-accent': 'Orange accent', 'brand-primary': 'Bleu',
      'brand-information': 'Cyan', 'brand-success': 'Vert', 'brand-warning': 'Jaune',
      'brand-danger': 'Rouge', 'neutral-900': 'Noir', 'neutral-white': 'Blanc',
    },
  },
  en: {
    open: 'Choose a color', dialog: 'Choose a color', current: 'current color',
    empty: 'none', none: 'None', noneLabel: 'No color',
    area: 'Saturation and brightness', areaRole: 'color area',
    areaText: 'Saturation {s}%, brightness {v}%', hue: 'Hue', options: 'Swatches',
    eyedropper: 'Eyedropper: pick a color from the screen',
    invalid: 'Invalid color (format #RRGGBB)', required: 'Choose a color',
    tokens: {
      'brand-upjv': 'UPJV blue', 'brand-accent': 'Accent orange', 'brand-primary': 'Blue',
      'brand-information': 'Cyan', 'brand-success': 'Green', 'brand-warning': 'Yellow',
      'brand-danger': 'Red', 'neutral-900': 'Black', 'neutral-white': 'White',
    },
  },
};

const ICON_EYEDROPPER = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m2 22 1-1h3l9-9"/><path d="M3 21v-3l9-9"/><path d="m15 6 3.4-3.4a2.1 2.1 0 1 1 3 3L18 9l.4.4a2.1 2.1 0 1 1-3 3l-3.8-3.8a2.1 2.1 0 1 1 3-3l.4.4Z"/></svg>';

// Nuances par défaut : primitives du DS, converties en hex au chargement
const DEFAULT_TOKENS = ['brand-upjv', 'brand-accent', 'brand-primary', 'brand-information', 'brand-success', 'brand-warning', 'brand-danger', 'neutral-900', 'neutral-white'];

// ---------- Couleurs ----------

const hex2 = (n) => Math.round(n).toString(16).padStart(2, '0').toUpperCase();
const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

/** '#RRGGBB' | 'RRGGBB' | '#RGB' | 'RGB' → '#RRGGBB' (majuscules), sinon null */
export function normalizeHex(text) {
  const m = /^\s*#?([0-9a-f]{3}|[0-9a-f]{6})\s*$/i.exec(text || '');
  if (!m) return null;
  const h = m[1].length === 3 ? [...m[1]].map((c) => c + c).join('') : m[1];
  return `#${h.toUpperCase()}`;
}

function hexToRgb(hex) {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function rgbToHsv([r, g, b]) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), d = max - Math.min(r, g, b);
  let h = 0;
  if (d) {
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h = (h * 60 + 360) % 360;
  }
  return { h, s: max ? (d / max) * 100 : 0, v: max * 100 };
}

function hsvToHex({ h, s, v }) {
  s /= 100; v /= 100;
  const f = (n) => { const k = (n + h / 60) % 6; return (v - v * s * Math.max(0, Math.min(k, 4 - k, 1))) * 255; };
  return `#${hex2(f(5))}${hex2(f(3))}${hex2(f(1))}`;
}

// Couleur CSS calculée (oklch, light-dark()…) → '#RRGGBB', via un canvas 1×1
let probeCtx = null;
function cssToHex(color) {
  if (!probeCtx) {
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = 1;
    probeCtx = canvas.getContext('2d', { willReadFrequently: true });
  }
  if (!probeCtx) return null;
  probeCtx.clearRect(0, 0, 1, 1);
  probeCtx.fillStyle = 'rgba(0, 0, 0, 0)'; // reste transparent si la couleur n'est pas comprise
  probeCtx.fillStyle = color;
  probeCtx.fillRect(0, 0, 1, 1);
  const [r, g, b, a] = probeCtx.getImageData(0, 0, 1, 1).data;
  return a ? `#${hex2(r)}${hex2(g)}${hex2(b)}` : null;
}

function tokenSwatches(L) {
  const probe = document.createElement('span');
  probe.style.display = 'none';
  document.body.append(probe);
  const list = DEFAULT_TOKENS.map((token) => {
    probe.style.color = `var(--${token})`;
    const hex = cssToHex(getComputedStyle(probe).color);
    return hex ? { value: hex, label: L.tokens[token] } : null;
  }).filter(Boolean);
  probe.remove();
  return list;
}

/** data-swatches="#022E96:Bleu UPJV,#DCEBFA" → [{ value, label }] */
function parseSwatches(attr) {
  return attr.split(',').map((item) => {
    const [code, ...rest] = item.split(':');
    const value = normalizeHex(code);
    return value ? { value, label: rest.join(':').trim() || null } : null;
  }).filter(Boolean);
}

// ---------- Initialisation ----------

export function initColorFields(container = document) {
  const roots = [...container.querySelectorAll('.color-field')];
  if (container.matches?.('.color-field')) roots.unshift(container);
  roots.forEach((root) => {
    if (root.dataset.colorFieldBound) return;
    const input = root.querySelector('input.color-field__input');
    if (!input) return;
    root.dataset.colorFieldBound = 'true';
    setupField(root, input);
  });
}

function setupField(root, input) {
  const locale = root.closest('[lang]')?.getAttribute('lang') || 'fr';
  const L = LABELS[locale.slice(0, 2).toLowerCase()] || LABELS.fr;
  const id = ++uid;
  const allowEmpty = root.hasAttribute('data-allow-empty');

  // ---------- DOM ----------
  input.type = 'text';
  input.autocomplete = 'off';
  input.spellcheck = false;
  input.maxLength = 7;
  if (!input.placeholder) input.placeholder = '#RRGGBB';

  let wrapper = root.querySelector('.color-field__wrapper');
  if (!wrapper) {
    wrapper = document.createElement('div');
    wrapper.className = 'color-field__wrapper';
    input.before(wrapper);
    wrapper.append(input);
  }

  const swatch = document.createElement('button');
  swatch.type = 'button';
  swatch.className = 'color-field__swatch';
  swatch.setAttribute('aria-haspopup', 'dialog');
  swatch.setAttribute('aria-expanded', 'false');
  wrapper.prepend(swatch);

  const popover = document.createElement('div');
  popover.className = 'color-field__popover';
  popover.id = `color-field-${id}-popover`;
  popover.setAttribute('role', 'dialog');
  popover.setAttribute('aria-label', L.dialog);
  const supportsPopover = typeof popover.showPopover === 'function';
  if (supportsPopover) popover.setAttribute('popover', 'auto');
  else popover.hidden = true;
  swatch.setAttribute('aria-controls', popover.id);
  root.append(popover);

  // Pipette : API EyeDropper (Chrome / Edge) ; bouton absent ailleurs
  const hasEyeDropper = typeof window !== 'undefined' && 'EyeDropper' in window;
  popover.innerHTML = `
    <div class="color-field__area" tabindex="0" role="slider" aria-label="${L.area}" aria-roledescription="${L.areaRole}" aria-valuemin="0" aria-valuemax="100">
      <span class="color-field__thumb"></span>
    </div>
    <div class="color-field__body">
      <div class="color-field__controls">
        ${hasEyeDropper ? `<button type="button" class="color-field__eyedropper" aria-label="${L.eyedropper}" title="${L.eyedropper}">${ICON_EYEDROPPER}</button>` : ''}
        <input type="range" class="color-field__hue" min="0" max="359" step="1" aria-label="${L.hue}">
      </div>
      <div class="color-field__options" role="group" aria-label="${L.options}"></div>
      ${allowEmpty ? `<div class="color-field__footer"><button type="button" class="btn btn-sm btn-link color-field__none" aria-label="${L.noneLabel}">${L.none}</button></div>` : ''}
    </div>`;
  const area = popover.querySelector('.color-field__area');
  const thumb = popover.querySelector('.color-field__thumb');
  const hue = popover.querySelector('.color-field__hue');
  const optionsBox = popover.querySelector('.color-field__options');
  const noneBtn = popover.querySelector('.color-field__none');

  const helper = root.querySelector('.color-field__helper');
  if (helper) {
    helper.id ||= `color-field-${id}-helper`;
    input.setAttribute('aria-describedby', helper.id);
  }

  // Nuances : data-swatches, sinon tokens du DS (data-swatches="" : aucune)
  let swatches = [];
  function buildOptions() {
    const attr = root.getAttribute('data-swatches');
    swatches = attr !== null ? parseSwatches(attr) : tokenSwatches(L);
    optionsBox.hidden = !swatches.length;
    optionsBox.replaceChildren(...swatches.map((sw) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'color-field__option';
      b.dataset.value = sw.value;
      b.style.background = sw.value;
      b.setAttribute('aria-label', sw.label ? `${sw.label} (${sw.value})` : sw.value);
      b.setAttribute('aria-pressed', 'false');
      return b;
    }));
  }
  buildOptions();

  // ---------- État ----------
  let current = normalizeHex(VALUE.get.call(input)) || '';
  let hsv = rgbToHsv(hexToRgb(current || '#000000'));
  let internal = false;

  function writeInput(value) {
    internal = true;
    VALUE.set.call(input, value);
    internal = false;
  }

  function emit(type) {
    internal = true;
    input.dispatchEvent(new Event(type, { bubbles: true }));
    internal = false;
    if (type === 'change') root.dispatchEvent(new CustomEvent('ds-color:change', { bubbles: true, detail: { value: current } }));
  }

  // Affichage (pastille, zone, teinte, nuances) d'après current / hsv
  function render() {
    const name = swatches.find((sw) => sw.value === current)?.label;
    swatch.style.background = current || '';
    swatch.setAttribute('aria-label', `${L.open} (${L.current} : ${current ? (name ? `${name}, ${current}` : current) : L.empty})`);
    popover.style.setProperty('--cf-hue', Math.round(hsv.h));
    thumb.style.left = `${hsv.s}%`;
    thumb.style.top = `${100 - hsv.v}%`;
    thumb.style.background = current || hsvToHex(hsv);
    area.setAttribute('aria-valuenow', Math.round(hsv.s));
    area.setAttribute('aria-valuetext', L.areaText.replace('{s}', Math.round(hsv.s)).replace('{v}', Math.round(hsv.v)));
    hue.value = Math.round(hsv.h);
    hue.setAttribute('aria-valuetext', `${Math.round(hsv.h)}°`);
    optionsBox.querySelectorAll('.color-field__option').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.value === current)));
    noneBtn?.setAttribute('aria-pressed', String(!current));
  }

  // Nouvelle valeur valide ('' = vide) ; garde la teinte si la couleur est grise
  function setCurrent(value, fromHsv = null) {
    current = value;
    if (fromHsv) hsv = fromHsv;
    else if (value) {
      const next = rgbToHsv(hexToRgb(value));
      hsv = next.s && next.v ? next : { h: hsv.h, s: next.s, v: next.v };
    }
    render();
  }

  // ---------- Erreurs ----------
  let ownError = false;
  function setError(message) {
    root.querySelector('.color-field__error[data-color-feedback]')?.remove();
    input.setCustomValidity(message || '');
    if (!message) {
      if (ownError) root.classList.remove('color-field--error');
      ownError = false;
      input.removeAttribute('aria-invalid');
      if (helper) input.setAttribute('aria-describedby', helper.id);
      else input.removeAttribute('aria-describedby');
      return;
    }
    const el = document.createElement('span');
    el.className = 'color-field__error';
    el.dataset.colorFeedback = '';
    el.id = `color-field-${id}-error`;
    el.setAttribute('role', 'alert');
    el.textContent = message;
    (helper || wrapper).after(el);
    root.classList.add('color-field--error');
    ownError = true;
    input.setAttribute('aria-invalid', 'true');
    input.setAttribute('aria-describedby', [helper?.id, el.id].filter(Boolean).join(' '));
  }

  // Saisie clavier validée (change natif / Entrée). Renvoie false si invalide.
  function commitText() {
    const text = VALUE.get.call(input).trim();
    if (!text) {
      if (!allowEmpty) { setError(input.required ? L.required : L.invalid); return false; }
      setError('');
      writeInput('');
      const changed = current !== '';
      setCurrent('');
      return changed ? 'changed' : true;
    }
    const value = normalizeHex(text);
    if (!value) { setError(L.invalid); return false; }
    setError('');
    writeInput(value);
    const changed = value !== current;
    setCurrent(value);
    return changed ? 'changed' : true;
  }

  // change natif de la saisie : normalisé avant que l'application ne le reçoive
  // (capture sur la racine) ; saisie invalide → change stoppé, dernière valeur gardée.
  // ds-color:change part à la remontée, après les écouteurs de change de l'input.
  let typedChange = false;
  root.addEventListener('change', (e) => {
    if (e.target !== input || internal) return;
    const result = commitText();
    // Invalide, ou valeur inchangée une fois normalisée (déjà validée par Entrée) : pas de change
    if (result !== 'changed') { e.stopImmediatePropagation(); return; }
    typedChange = true;
  }, true);
  root.addEventListener('change', (e) => {
    if (e.target !== input || !typedChange) return;
    typedChange = false;
    root.dispatchEvent(new CustomEvent('ds-color:change', { bubbles: true, detail: { value: current } }));
  });

  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      const before = current;
      const result = commitText();
      if (result && current !== before) emit('change');
      if (!result) e.preventDefault();
    } else if (e.key === 'ArrowDown' && e.altKey) { e.preventDefault(); open(); }
  });

  // ---------- Sélection dans le popover ----------
  // live : pendant le geste (input) ; à la fin (change + ds-color:change)
  let dirty = false;
  function pickHsv(next, final) {
    const value = hsvToHex(next);
    setError('');
    if (value !== current) {
      writeInput(value);
      setCurrent(value, next);
      emit('input');
      dirty = true;
    } else { hsv = next; render(); }
    if (final && dirty) { dirty = false; emit('change'); }
  }

  function pickValue(value) {
    setError('');
    if (value === current) return;
    writeInput(value);
    setCurrent(value);
    emit('input');
    emit('change');
  }

  // Zone saturation / luminosité : pointeur
  function fromPointer(e) {
    const r = area.getBoundingClientRect();
    return {
      h: hsv.h,
      s: clamp(((e.clientX - r.left) / r.width) * 100, 0, 100),
      v: clamp(100 - ((e.clientY - r.top) / r.height) * 100, 0, 100),
    };
  }
  area.addEventListener('pointerdown', (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    area.focus();
    area.setPointerCapture(e.pointerId);
    pickHsv(fromPointer(e), false);
  });
  area.addEventListener('pointermove', (e) => { if (area.hasPointerCapture(e.pointerId)) pickHsv(fromPointer(e), false); });
  area.addEventListener('pointerup', (e) => { if (area.hasPointerCapture(e.pointerId)) pickHsv(fromPointer(e), true); });

  // Zone : clavier (flèches ±1, Maj ±10, Début/Fin saturation, Page luminosité)
  area.addEventListener('keydown', (e) => {
    const step = e.shiftKey ? 10 : 1;
    const moves = {
      ArrowLeft: { s: -step }, ArrowRight: { s: step }, ArrowDown: { v: -step }, ArrowUp: { v: step },
      Home: { s: -100 }, End: { s: 100 }, PageDown: { v: -10 }, PageUp: { v: 10 },
    }[e.key];
    if (moves) {
      e.preventDefault();
      pickHsv({ h: hsv.h, s: clamp(hsv.s + (moves.s || 0), 0, 100), v: clamp(hsv.v + (moves.v || 0), 0, 100) }, true);
    } else if (e.key === 'Enter') { e.preventDefault(); close(true); }
  });

  // Teinte : input pendant le glisser / flèches, change à la fin
  hue.addEventListener('input', (e) => { e.stopPropagation(); pickHsv({ ...hsv, h: +hue.value }, false); });
  hue.addEventListener('change', (e) => { e.stopPropagation(); pickHsv({ ...hsv, h: +hue.value }, true); });

  popover.querySelector('.color-field__eyedropper')?.addEventListener('click', async () => {
    try {
      const { sRGBHex } = await new window.EyeDropper().open();
      const value = normalizeHex(sRGBHex) || cssToHex(sRGBHex);
      if (value) pickValue(value);
    } catch (e) { /* prélèvement annulé (Échap) */ }
    if (isOpen()) area.focus();
  });

  popover.addEventListener('click', (e) => {
    const option = e.target.closest('.color-field__option');
    if (option) { pickValue(option.dataset.value); return; }
    if (e.target.closest('.color-field__none')) { pickValue(''); close(true); }
  });

  // ---------- Popover ----------
  const isOpen = () => (supportsPopover ? popover.matches(':popover-open') : !popover.hidden);

  function open() {
    if (swatch.disabled || isOpen()) return;
    if (supportsPopover) popover.showPopover();
    else { render(); popover.hidden = false; afterOpen(); }
  }

  function close(focusSwatch = false) {
    if (!isOpen()) return;
    if (supportsPopover) popover.hidePopover();
    else { popover.hidden = true; afterClose(); }
    if (focusSwatch) swatch.focus();
  }

  function position() {
    const r = wrapper.getBoundingClientRect();
    const p = popover.getBoundingClientRect();
    const gap = 4, margin = 8;
    let top = r.bottom + gap;
    if (top + p.height > innerHeight - margin && r.top - p.height - gap > margin) top = r.top - p.height - gap;
    const left = Math.max(margin, Math.min(r.left, innerWidth - p.width - margin));
    popover.style.top = `${Math.round(top)}px`;
    popover.style.left = `${Math.round(left)}px`;
  }

  const onViewport = () => position();
  function afterOpen() {
    swatch.setAttribute('aria-expanded', 'true');
    position();
    addEventListener('scroll', onViewport, true);
    addEventListener('resize', onViewport);
    (popover.querySelector('.color-field__option[aria-pressed="true"]') || area).focus();
  }
  function afterClose() {
    swatch.setAttribute('aria-expanded', 'false');
    removeEventListener('scroll', onViewport, true);
    removeEventListener('resize', onViewport);
    if (dirty) { dirty = false; emit('change'); }
    // Focus rendu à la pastille s'il était dans le popover (clic extérieur : on ne le vole pas)
    if (popover.contains(document.activeElement) || document.activeElement === document.body) swatch.focus();
  }

  if (supportsPopover) {
    swatch.popoverTargetElement = popover;
    swatch.popoverTargetAction = 'toggle';
    popover.addEventListener('beforetoggle', (e) => { if (e.newState === 'open') render(); });
    popover.addEventListener('toggle', (e) => { if (e.newState === 'open') afterOpen(); else afterClose(); });
  } else {
    swatch.addEventListener('click', () => (isOpen() ? close() : open()));
    document.addEventListener('click', (e) => { if (isOpen() && !root.contains(e.target)) close(); });
  }

  popover.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(true); }
  });

  // ---------- Synchronisation ----------
  // input.value = '…' par l'application : pastille mise à jour (comme un champ natif, sans événement)
  function sync() {
    const raw = VALUE.get.call(input);
    const value = normalizeHex(raw);
    if (value || !raw) setCurrent(value || '');
    if (value && raw !== value) writeInput(value);
    swatch.disabled = input.disabled || input.readOnly;
    if (ownError) setError('');
  }

  Object.defineProperty(input, 'value', {
    configurable: true,
    enumerable: true,
    get() { return VALUE.get.call(this); },
    set(v) { VALUE.set.call(this, v); if (!internal) sync(); },
  });
  new MutationObserver(sync).observe(input, { attributes: true, attributeFilter: ['disabled', 'readonly'] });
  new MutationObserver(() => { buildOptions(); render(); }).observe(root, { attributes: true, attributeFilter: ['data-swatches'] });
  input.form?.addEventListener('reset', () => setTimeout(sync));

  // ---------- API ----------
  root.dsSetValue = (value, { emit: notify = false } = {}) => {
    const v = value ? normalizeHex(value) : '';
    if (v === null) return;
    const changed = v !== current;
    writeInput(v);
    setError('');
    setCurrent(v);
    if (notify && changed) { emit('input'); emit('change'); }
  };
  root.dsClear = (opts) => root.dsSetValue('', opts);
  root.dsOpen = open;
  root.dsClose = () => close(false);
  root.dsUpdate = () => { buildOptions(); sync(); };

  sync();
}
